import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@blueprintjs/core";
import DecoyImg from "./assets/Decoy.png";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors } from "../../../../components/colors";
import { DecoyEffect } from "../../../../translations/experiments/DecoyEffect";

// Import glossary terms from translations (multi-language)
import { de as deTranslations } from "../../../../translations/de";
import { en as enTranslations } from "../../../../translations/en";

// --- Helper: Get glossary for current language ---
function useGlossaryTerms(language: string) {
    if (language === "de") {
        return deTranslations.glossary?.terms || {};
    } else if (language === "en") {
        return enTranslations.glossary?.terms || {};
    }
    // fallback: use German
    return deTranslations.glossary?.terms || {};
}

// --- Hilfsfunktion für Glossar-Alias-Auflösung (language-agnostic) ---
function resolveGlossarEntry(
    glossar: Record<string, any>,
    key: string
): { key: string; text: string } | undefined {
    const seen = new Set<string>();
    let current = key;
    while (current && !seen.has(current)) {
        seen.add(current);
        const entry = glossar[current];
        if (typeof entry === "string") return { key: current, text: entry };
        if (entry && typeof entry === "object" && "alias" in entry) {
            current = entry.alias;
        } else {
            break;
        }
    }
    return undefined;
}

// Tooltip für Glossar-Begriff (alias-sicher, language-agnostic!)
const BegriffTooltip: React.FC<{ begriff: string; children: React.ReactNode; glossar: Record<string, any> }> = ({ begriff, children, glossar }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!visible) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setVisible(false);
            }
        };
        window.addEventListener("mousedown", onClick);
        return () => window.removeEventListener("mousedown", onClick);
    }, [visible]);

    const resolved = resolveGlossarEntry(glossar, begriff);

    return (
        <span
            ref={ref}
            style={{
                position: "relative",
                cursor: "pointer",
                background: colors.lichtBlauLighter,
                color: colors.brillantBlue,
                borderRadius: 5,
                padding: "0 4px",
                fontWeight: 500,
                zIndex: 10,
                display: "inline-block"
            }}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onClick={e => {
                setVisible((v: boolean) => !v);
                e.stopPropagation();
            }}
            tabIndex={0}
            aria-label={`Begriffserklärung für ${begriff}`}
        >
            <>
                {children}
                {visible && (
                    <span
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: "100%",
                            transform: "translate(-50%, 12px)",
                            minWidth: 320,
                            maxWidth: 600,
                            background: colors.white,
                            color: colors.textDark,
                            border: `1.5px solid ${colors.lichtBlauLight}`,
                            borderRadius: 14,
                            boxShadow: "0 10px 38px 0 rgba(0,0,0,0.17)",
                            zIndex: 1050,
                            padding: "22px 24px",
                            fontWeight: 400,
                            fontSize: 16,
                            lineHeight: 1.6,
                            pointerEvents: "auto",
                            whiteSpace: "pre-line",
                            marginTop: 12,
                            textAlign: "justify"
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <b>{resolved?.key ?? begriff}</b>
                        <br />
                        {resolved?.text ?? begriff}
                    </span>
                )}
            </>
        </span>
    );
};

// Case-insensitive glossary parsing using the correct language
function parseWithGlossarTooltip(
    text: string,
    terms: string[],
    used: Set<string>,
    glossar: Record<string, any>
) {
    if (typeof text !== "string") return null;
    const glossaryKeyMap: Record<string, string> = {};
    terms.forEach(k => { glossaryKeyMap[k.toLowerCase()] = k; });

    const re = new RegExp(
        `\\b(${terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`,
        "gi"
    );
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let i = 0;

    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        const matched = match[1];
        const glossaryKey = glossaryKeyMap[matched.toLowerCase()] || matched;

        if (!used.has(glossaryKey)) {
            used.add(glossaryKey);
            parts.push(
                <BegriffTooltip begriff={glossaryKey} glossar={glossar} key={glossaryKey + "-" + i}>
                    {matched}
                </BegriffTooltip>
            );
        } else {
            parts.push(matched);
        }
        lastIndex = match.index + matched.length;
        i++;
    }
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    return parts;
}

export const DecoyExplainationScreenComponent: React.FC = () => {
    const { language } = useLanguage();
    const glossar = useGlossaryTerms(language);
    const txt = DecoyEffect[language]?.screens?.explanation;

    const sectionTitles = txt?.sections?.map((s: any) => s.title) || [
        "Was ist der Decoy-Effekt?",
        "Wie wird dieser Effekt ausgenutzt?",
        "Warum ist der Decoy-Effekt besonders?",
        "Bekanntes Forschungsbeispiel"
    ];
    const paragraphs = txt?.sections?.map((s: any) => s.paragraph) || [
        `Im Alltag treffen wir oft Entscheidungen zwischen mehreren Angeboten. Manchmal gibt es eine Auswahl, bei der eine Option sehr teuer oder wenig attraktiv wirkt. Durch diese Köder-Option wirkt ein anderes Angebot plötzlich viel besser. Das nennt man Decoy-Effekt. Unternehmen nutzen diesen Trick, damit wir uns eher für das Angebot entscheiden, das sie verkaufen wollen.`,
        `Stell dir vor, du möchtest etwas kaufen und bekommst drei Möglichkeiten: Eine günstige, eine teure und eine unattraktive, die etwas dazwischen liegt, aber eigentlich keinen Sinn ergibt. Die mittlere Option ist so gestaltet, dass sie das teure Angebot besser erscheinen lässt. Auch wenn fast niemand die mittlere wählt, hilft sie dabei, die teure Option beliebter zu machen. So beeinflussen Unternehmen unsere Entscheidung, ohne dass wir es merken.`,
        `Eigentlich sagt eine wichtige Regel aus der Entscheidungstheorie, dass ein Angebot nicht plötzlich beliebter werden sollte, nur weil eine weitere Auswahl dazukommt. Und wenn ein ähnliches Produkt dazu kommt, sollte das ursprüngliche Angebot sogar weniger interessant werden. Beim Decoy-Effekt ist das anders: Die neue, unattraktive Option sorgt dafür, dass das gewünschte Angebot besonders gut dasteht.`,
        `Forscher haben gezeigt, wie das funktioniert: Bei einer Studie konnten Menschen zwischen zwei Kameras wählen, einer günstigen und einer teuren. Als eine dritte, unattraktive Kamera dazu kam, entschieden sich plötzlich mehr Leute für die teure Kamera. Die Köder-Option hat die Entscheidung also beeinflusst, obwohl sie kaum jemand wirklich gekauft hat.`
    ];
    const experimentNote = txt?.experimentNote || `<b>Wie war das in eurem Burger-Experiment?</b><br /><br />
        Ihr habt entweder bei den Getränken oder bei den Pommes mal zwei und mal drei Auswahlmöglichkeiten bekommen. Die Gruppe mit drei Wahlmöglichkeiten hatte jeweils eine Köder-Option.<br /><br />
        Das Besondere: Nicht alle hatten gleichzeitig einen Decoy, sondern nur bei einer von beiden Entscheidungen. So konnte man sehen, wie die zusätzliche, unattraktive Option eure Wahl beeinflusst hat, auch wenn sie kaum jemand genommen hat.<br /><br />
        Der Decoy-Effekt zeigt sich also darin, dass die Köder-Option eine andere Auswahl attraktiver macht, die sogar teurer sein kann!`;
    const sources = txt?.sources || [
        "Huber, J., Payne, J. W., & Puto, C. (1982). Adding asymmetrically dominated alternatives: Violations of regularity and the similarity hypothesis. Journal of Consumer Research, 9(1), 90–98.",
        "Ariely, D. (2008). Predictably Irrational: The Hidden Forces That Shape Our Decisions. HarperCollins.",
        "Rieskamp, J., Busemeyer, J. R., & Mellers, B. A. (2006). Extending the Bounds of Rationality: Evidence and Theories of Preferential Choice. Journal of Economic Literature, 44(3), 631–661."
    ];
    const headline = txt?.headline || "Wie beeinflusst der Decoy-Effekt unsere Entscheidungen?";

    // Parse and render the main content with glossary tooltips
    function parseParagraphsWithSections() {
        const terms = Object.keys(glossar).sort((a, b) => b.length - a.length);
        const used = new Set<string>();
        const parsed: React.ReactNode[] = [];

        for (let i = 0; i < sectionTitles.length; ++i) {
            parsed.push(
                <div key={"section-title-" + i} style={{
                    marginTop: i === 0 ? 0 : 30,
                    marginBottom: 8
                }}>
                    <b style={{
                        fontSize: 20,
                        color: colors.brillantBlue,
                        fontWeight: 600
                    }}>
                        {sectionTitles[i]}
                    </b>
                </div>
            );
            // Bild nur im ersten Abschnitt, oben rechts
            if (i === 0) {
                parsed.push(
                    <img
                        src={DecoyImg}
                        alt="Illustration Decoy-Effekt"
                        style={{
                            float: "right",
                            marginLeft: 44,
                            marginBottom: 18,
                            width: 260,
                            maxWidth: "30%",
                            borderRadius: 16,
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)",
                            objectFit: "cover"
                        }}
                    />
                );
            }
            parsed.push(
                <p key={`p-${i}`} style={{
                    marginTop: 0,
                    marginBottom: 18,
                    whiteSpace: "pre-line",
                    color: colors.textMedium,
                    fontSize: 17,
                    lineHeight: 1.7,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    {parseWithGlossarTooltip(paragraphs[i], terms, used, glossar)}
                </p>
            );
        }
        return parsed;
    }

    return (
        <div className="experiment-container" style={{
            maxWidth: 1250,
            margin: "0 auto",
            padding: "24px 20px 44px",
            backgroundColor: colors.backgroundLight
        }}>
            {/* Main content card */}
            <div style={{
                background: colors.white,
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                padding: "24px 40px 36px",
                marginBottom: 28
            }}>
                {/* Heading */}
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {headline}
                </h2>
                <div style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    position: "relative",
                    minHeight: 320
                }}>
                    {parseParagraphsWithSections()}
                </div>

                {/* Experiment note in a box */}
                <div style={{
                    marginTop: 38,
                    marginBottom: 38,
                    background: colors.backgroundLight,
                    borderRadius: 11,
                    padding: "26px 32px",
                    fontSize: 17,
                    lineHeight: 1.7,
                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.06)",
                    color: colors.textMedium,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: 16
                    }}>
                        <Icon
                            icon="lab-test"
                            size={20}
                            style={{
                                marginRight: 12,
                                marginTop: 2,
                                color: colors.brillantBlue
                            }}
                        />
                        <span style={{
                            fontWeight: 600,
                            fontSize: 18,
                            color: colors.brillantBlue
                        }}>
                            {language === "de" ? "Forschungsbeispiel" : "Research example"}
                        </span>
                    </div>
                    <span dangerouslySetInnerHTML={{ __html: experimentNote }} />
                </div>

                {/* Sources */}
                <div style={{
                    background: colors.backgroundLight,
                    borderRadius: 8,
                    padding: "22px 24px",
                    marginTop: 36,
                    fontSize: 15.5,
                    color: colors.textMedium
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 8
                    }}>
                        <Icon
                            icon="book"
                            size={16}
                            style={{
                                marginRight: 10,
                                color: colors.brillantBlue
                            }}
                        />
                        <span style={{
                            fontWeight: 600,
                            color: colors.brillantBlue
                        }}>
                            {language === "de" ? "Quellen" : "Sources"}
                        </span>
                    </div>
                    <ul style={{
                        marginTop: 8,
                        marginBottom: 0,
                        paddingLeft: 24,
                        textAlign: "justify",
                        textJustify: "inter-word"
                    }}>
                        {sources.map((src, index) => (
                            <li key={`source-${index}`} style={{
                                marginBottom: index < sources.length - 1 ? 8 : 0
                            }}>
                                {src}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export const DecoyExplainationScreen: ScreenConfig = {
    component: DecoyExplainationScreenComponent,
    defaultResult: {},
    canContinueDefault: true,
};